import React, { Component } from 'react';
import Inherit from '../../../core/inherit';
import CampaignController from './campaign.controller';
import withNavigate from '../../../core/withNavigate';
import { Layout, TableSkeleton, AnalyticsSkeleton, ChannelFilter, PageHeader, StatCard, StatusTag, DetailFact } from '../../common';
import { Button } from 'antd';
import { Flag, Pencil, Store, CalendarClock, CalendarCheck, DollarSign, Tag as TagIcon, TriangleAlert } from 'lucide-react';
import dayjs from 'dayjs';
import { CampaignAnalytics, CampaignStoreResults, CampaignImageGallery } from './elements';
import { formatCurrency, formatNumber } from '../../../utils/format';
import { ALL_CHANNELS } from '../../../utils/storeChannel';
import { isFeatureEnabled } from '../../../company/session';
import { FEATURES } from '../../../company/features';

class ShelfCampaign extends Inherit(CampaignController, Component) {
    state = {
        campaign: null,
        contracts: null,
        loading: true,
        channel: ALL_CHANNELS
    };

    render() {
        const { campaign, loading, channel } = this.state;

        if (loading) {
            return (
                <Layout current="campaigns">
                    <PageHeader icon={<Flag />} iconTone="violet" title="Detalle de Campaña" description="Cargando..." />
                    <div className="space-y-4">
                        <AnalyticsSkeleton count={4} />
                        <TableSkeleton rows={6} columns={5} />
                    </div>
                </Layout>
            );
        }

        const { filteredCampaign, filteredContracts, analytics, isExpired, isClientUser, totalInvestment, channelCounts, materials } = this.getViewModel();

        return (
            <Layout current="campaigns">
                <PageHeader
                    icon={<Flag />}
                    iconTone="violet"
                    title={campaign?.name || 'Campaña'}
                    description="Detalle y resultados de la campaña"
                    meta={
                        <>
                            <StatusTag tone={campaign?.campaignType === 'pop_blando' ? 'info' : 'violet'}>
                                {campaign?.campaignType === 'pop_blando' ? 'POP Blando' : 'POP Duro'}
                            </StatusTag>
                            {isExpired ? (
                                <StatusTag tone="warning" icon={<TriangleAlert />}>
                                    Expirada
                                </StatusTag>
                            ) : (
                                <StatusTag tone={campaign?.active ? 'success' : 'danger'} dot>
                                    {campaign?.active ? 'Activa' : 'Inactiva'}
                                </StatusTag>
                            )}
                            <DetailFact icon={<TagIcon />} label="Materiales">
                                {materials.length ? (
                                    <span className="inline-flex flex-wrap gap-1">
                                        {materials.map((material, idx) => (
                                            <StatusTag key={`${material}-${idx}`} tone="neutral">
                                                {material}
                                            </StatusTag>
                                        ))}
                                    </span>
                                ) : (
                                    'N/A'
                                )}
                            </DetailFact>
                        </>
                    }
                    actions={
                        // Edit — hidden for client users
                        !isClientUser && (
                            <Button type="primary" icon={<Pencil />} onClick={() => this.handleEdit()}>
                                Editar Campaña
                            </Button>
                        )
                    }
                />

                <div className="space-y-4">
                    {/* Channel Filter — applies to every section below */}
                    <ChannelFilter
                        value={channel}
                        onChange={(next) => this.handleChannelChange(next)}
                        counts={channelCounts}
                        visibleStores={filteredCampaign?.stores?.length || 0}
                        totalStores={campaign?.stores?.length || 0}
                    />

                    {/* Key facts & investment */}
                    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                        <StatCard label="Inversión Total" value={formatCurrency(totalInvestment)} icon={<DollarSign />} tone="success" hint="Suma de los pisos visibles" />
                        <StatCard label="Pisos Asignados" value={formatNumber(analytics.totalStores)} icon={<Store />} tone="violet" hint="Pisos de venta en la campaña" />
                        {campaign?.startDate && (
                            <StatCard
                                label="Inicio"
                                value={dayjs(campaign.startDate).format('DD/MM/YYYY')}
                                icon={<CalendarCheck />}
                                tone="info"
                                hint="Fecha de inicio"
                            />
                        )}
                        <StatCard
                            label="Vencimiento"
                            value={campaign?.expirationDate ? dayjs(campaign.expirationDate).format('DD/MM/YYYY') : 'N/A'}
                            valueTone={isExpired ? 'danger' : undefined}
                            icon={<CalendarClock />}
                            tone={isExpired ? 'danger' : 'warning'}
                            hint={isExpired ? 'Campaña expirada' : 'Fecha de vencimiento'}
                        />
                    </div>

                    {/* Compliance */}
                    <CampaignAnalytics analytics={analytics} campaign={filteredCampaign} />

                    {/* Store Results Section */}
                    <CampaignStoreResults
                        campaign={filteredCampaign}
                        contracts={filteredContracts}
                        navigateStore={this.navigateStore.bind(this)}
                        onStorePriceChange={this.updateStorePrice.bind(this)}
                        canEditPrices={!isClientUser && isFeatureEnabled(FEATURES.TRADE_STORE_PRICES)}
                    />

                    {/* Image Gallery Section */}
                    <CampaignImageGallery campaign={filteredCampaign} contracts={filteredContracts} />
                </div>
            </Layout>
        );
    }
}

export default withNavigate(ShelfCampaign);
